import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
// DTC-L: route OptiBay training lifecycle elapsed math + completed_at write through clock.
import { clock } from '@/utils/clock';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, GraduationCap, CheckCircle2, Trophy } from 'lucide-react';
import { OptiBayTrainingHints } from './OptiBayTrainingHints';
import { useCreateLearningOutcome } from '@/hooks/useOptiBayLearningOutcome';
import { useUpdateOptiBaySession } from '@/hooks/useOptiBaySessions';
import { useSessionEvidence } from '@/hooks/useOptiBayEvidence';
import { useSessionTestPaths } from '@/hooks/useOptiBayTestPaths';
import { useSessionInterpretations } from '@/hooks/useOptiBayInterpretation';
import { useSessionRepairs } from '@/hooks/useOptiBayRepair';
import { useSessionVerifications } from '@/hooks/useOptiBayPostVerification';
import type { OptiBaySession, OptiBayOutcomeStatus } from '@/types/optibay';
import { cn } from '@/lib/utils';

interface OptiBayTrainingStepProps {
  session: OptiBaySession;
  onBack: () => void;
  onComplete: () => void;
}

const OUTCOME_OPTIONS: { value: OptiBayOutcomeStatus; label: string; description: string }[] = [
  { value: 'successful', label: 'Successful', description: 'Root cause identified and repaired correctly' },
  { value: 'partial', label: 'Partial', description: 'Issue partially resolved, may need follow-up' },
  { value: 'unsuccessful', label: 'Unsuccessful', description: 'Root cause not identified or repair did not resolve' },
  { value: 'inconclusive', label: 'Inconclusive', description: 'Unable to determine outcome at this time' },
];

export function OptiBayTrainingStep({ session, onBack, onComplete }: OptiBayTrainingStepProps) {
  const { data: evidence = [] } = useSessionEvidence(session.id);
  const { data: testPaths = [] } = useSessionTestPaths(session.id);
  const { data: interpretations = [] } = useSessionInterpretations(session.id);
  const { data: repairs = [] } = useSessionRepairs(session.id);
  const { data: verifications = [] } = useSessionVerifications(session.id);
  const createOutcome = useCreateLearningOutcome();
  const updateSession = useUpdateOptiBaySession();

  const [outcomeStatus, setOutcomeStatus] = useState<OptiBayOutcomeStatus>('successful');
  const [techNotes, setTechNotes] = useState('');
  const [isCompleting, setIsCompleting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const evidenceTypes = [...new Set(evidence.map(e => e.evidence_type))];
  const passedVerifications = verifications.filter(v => v.result === 'passed').length;
  const totalVerifications = verifications.length;
  const passRate = totalVerifications > 0 ? (passedVerifications / totalVerifications) * 100 : null;
  const primaryInterpretation = interpretations[0];

  const durationMinutes = Math.round(
    (clock.nowMs() - new Date(session.started_at).getTime()) / 60000
  );

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      // Create learning outcome
      await createOutcome.mutateAsync({
        sessionId: session.id,
        shopId: session.shop_id,
        technicianProfileId: session.technician_profile_id || undefined,
        systemFamilyId: session.system_family_id || undefined,
        symptomText: session.symptom_text || undefined,
        rootCauseCategory: primaryInterpretation?.root_cause_category || undefined,
        confidence: primaryInterpretation?.confidence || 'medium',
        evidenceTypes,
        evidenceCount: evidence.length,
        testPathCount: testPaths.length,
        verificationPassRate: passRate ?? undefined,
        repairCount: repairs.length,
        totalDurationMinutes: durationMinutes,
        outcomeStatus,
        technicianNotes: techNotes.trim() || undefined,
      });

      // Mark session completed
      await updateSession.mutateAsync({
        sessionId: session.id,
        updates: {
          status: 'completed',
          completed_at: clock.now().toISOString(),
        },
      });

      setIsCompleted(true);
    } catch (e) {
      console.error('Failed to complete session:', e);
    } finally {
      setIsCompleting(false);
    }
  };

  if (isCompleted) {
    return (
      <div className="text-center py-8 space-y-4">
        <Trophy className="h-12 w-12 mx-auto text-primary" />
        <h4 className="text-sm font-semibold">Diagnostic Session Complete</h4>
        <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
          <div className="text-center">
            <p className="text-lg font-bold text-primary">{evidence.length}</p>
            <p className="text-[10px] text-muted-foreground">Evidence</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-primary">{repairs.length}</p>
            <p className="text-[10px] text-muted-foreground">Repairs</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-primary">{durationMinutes}m</p>
            <p className="text-[10px] text-muted-foreground">Duration</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onComplete}>
          Close Session
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Complete & Learn</h4>
        <GraduationCap className="h-4 w-4 text-primary" />
      </div>

      <div className="rounded-md border border-primary/20 bg-primary/5 p-3 text-xs text-foreground">
        <GraduationCap className="h-3.5 w-3.5 inline mr-1.5 text-primary" />
        Review your session summary and record the outcome. This data silently improves future diagnostic suggestions.
      </div>

      {/* Session summary */}
      <div className="grid grid-cols-2 gap-2">
        <div className="border rounded-lg p-2.5 text-center">
          <p className="text-lg font-bold">{testPaths.length}</p>
          <p className="text-[10px] text-muted-foreground">Test Paths</p>
        </div>
        <div className="border rounded-lg p-2.5 text-center">
          <p className="text-lg font-bold">{evidence.length}</p>
          <p className="text-[10px] text-muted-foreground">Evidence Items</p>
        </div>
        <div className="border rounded-lg p-2.5 text-center">
          <p className="text-lg font-bold">{repairs.length}</p>
          <p className="text-[10px] text-muted-foreground">Repairs</p>
        </div>
        <div className="border rounded-lg p-2.5 text-center">
          <p className="text-lg font-bold">
            {passRate !== null ? `${Math.round(passRate)}%` : '—'}
          </p>
          <p className="text-[10px] text-muted-foreground">Verification Pass</p>
        </div>
      </div>

      {/* Outcome selection */}
      <div>
        <label className="text-xs font-medium mb-1.5 block">Diagnostic Outcome</label>
        <div className="space-y-1.5">
          {OUTCOME_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setOutcomeStatus(opt.value)}
              className={cn(
                'w-full text-left rounded-lg border p-2.5 transition-colors',
                outcomeStatus === opt.value
                  ? 'border-primary bg-primary/5'
                  : 'hover:bg-accent/50',
              )}
            >
              <div className="flex items-center gap-2">
                {outcomeStatus === opt.value && <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0" />}
                <div>
                  <p className="text-xs font-medium">{opt.label}</p>
                  <p className="text-[10px] text-muted-foreground">{opt.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Technician notes */}
      <div>
        <label className="text-xs font-medium mb-1 block">Technician Notes (optional)</label>
        <Textarea
          placeholder="Any additional observations or lessons learned…"
          value={techNotes}
          onChange={e => setTechNotes(e.target.value)}
          className="text-xs min-h-[60px]"
        />
      </div>

      <OptiBayTrainingHints session={session} className="mt-2" />

      <div className="flex justify-between pt-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" />
          Back
        </Button>
        <Button
          size="sm"
          onClick={handleComplete}
          disabled={isCompleting}
        >
          {isCompleting ? 'Completing…' : 'Complete Session'}
          <CheckCircle2 className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
}
